import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  Select, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import masterService from '../../../services/masterService';
import { useUser } from '../../../context/UserContext';

const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

const CountryList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} countries`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    searchText: '',
    status: 'all'
  });

  const getCountries = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.searchText,
        ...params
      };
      
      console.log('Fetching countries with params:', requestParams);
      const response = await masterService.getCountries(requestParams);
      console.log('Countries API Response:', response);
      
      if (response.data && response.data.status) {
        // Handle different possible response structures
        const countriesData = response.data.data?.data || response.data.data || [];
        const paginationData = response.data.data?.pagination || response.data.pagination || {};
        
        console.log('Countries data:', countriesData);
        console.log('Pagination data:', paginationData);
        
        // Ensure countriesData is an array
        if (Array.isArray(countriesData)) {
          setCountries(countriesData);
        } else {
          console.error('Countries data is not an array:', countriesData);
          setCountries([]);
        }
        
        setPagination(prev => ({
          ...prev,
          ...paginationData,
          current: paginationData.currentPage || 1
        }));
      } else {
        message.error('Failed to fetch countries');
        setCountries([]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      message.error('Failed to fetch countries');
      setCountries([]);
    } finally {
      setLoading(false);
    }
  }, [filters.searchText, filters.status]);

  // Load countries data
  useEffect(() => {
    getCountries();
  }, [getCountries]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getCountries({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1
    });
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getCountries({
      searchText: filters.searchText,
      status: value,
      perPage: pagination.pageSize,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getCountries({
      searchText: filters.searchText,
      status: filters.status,
      perPage: pageSize,
      currentPage: current
    });
  };

  // Handle add country
  const handleAddCountry = () => {
    navigate('/master/addcountry');
  };

  // Handle edit country
  const handleEditCountry = (countryId) => {
    navigate(`/master/editcountry/${countryId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'Country Name',
      dataIndex: 'country_name',
      key: 'country_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.country_name.localeCompare(b.country_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'country_is_active',
      key: 'country_is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditCountry(record.country_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Countries</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Country List"
      desc="Manage countries and their status"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Countries ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCountry}
              size="large"
            >
              Add Country
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Select
              value={filters.status}
              onChange={handleStatusChange}
              placeholder="Status"
              style={{ width: 150 }}
            >
              <Option value="all">All</Option>
              <Option value="Y">Active</Option>
              <Option value="N">Inactive</Option>
            </Select>
            
            <Search
              placeholder="Search countries..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={countries}
          rowKey="country_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{ 
            emptyText: 'No countries found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default CountryList;